import { Button, Menu, MenuItem, makeStyles } from "@material-ui/core";
import React, { useCallback, useContext, useMemo } from "react";
import { WorkflowContext, useRoles } from "ra-friendsofbabba";
import { green, red, yellow } from "@material-ui/core/colors";
import { useNotify, useTranslate, useUpdate } from "react-admin";

import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { get } from "lodash";

const STATE_COLORS = {
  to_be_checked: yellow[50],
  admissible: green[50],
  not_admissible: red[50],
  integrations_requested: yellow[50],
  integrations_received: yellow[50],
};

const useStyles = makeStyles((theme) => ({
  confirmButton: {
    marginTop: theme.spacing(1),
  },
}));

// Indica l'elenco degli stati condivisi ed utili alla valutazione di ammissibilità.
// Gli stati presenti di seguito sono anche contenuti nel workflow, questo elemento
// provvede a visionare appunto solo questi.
const SHARED_STATE_CODES = [
  "admissible",
  "not_admissible",
  "to_be_checked",
  "integrations_requested",
  "integrations_received",
];

const AdmissibilityStatusInput = ({ source, record, resource: toResolve }) => {
  const roles = useRoles();
  const classes = useStyles();
  const translate = useTranslate();

  // Se il componente è utilizzato all'interno di TransactionLogsField
  // è necessario risolvere il vero nome della risorsa partendo dalla risorsa
  // utilizzata per recuperare l'elenco dei logs.
  const resource = useMemo(
    () => toResolve.replace("workflow/transactions/", ""),
    [toResolve]
  );
  const admissibilityStatus = get(record, source, "to_be_checked");
  const { getWorkflow } = useContext(WorkflowContext);

  const { currentState, nextStates } = useMemo(() => {
    const workflow = getWorkflow(resource);
    const currentState = workflow
      ? workflow.getStateByCode(admissibilityStatus)
      : undefined;
    const nextStates = workflow
      .getNextStates(roles, record)
      .filter((s) => SHARED_STATE_CODES.indexOf(s.code) !== -1);
    return { currentState, nextStates };
  }, [roles, record, admissibilityStatus, getWorkflow, resource]);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [update, { loading }] = useUpdate();

  const handleClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = useCallback(() => setAnchorEl(null), []);
  const handleChange = useCallback(
    (state) => {
      setAnchorEl(null);
      update(
        "research-projects",
        record.id,
        {
          ...record,
          state: "admissibility_notes",
          admissibility_status: state.code,
        },
        record
      );
    },
    [update, record]
  );
  const transactionState = get(
    record,
    "state",
    get(record, "transaction.state")
  );
  const confirmed = transactionState === admissibilityStatus;
  const notify = useNotify();
  const handleConfirmState = useCallback(
    () =>
      update(
        "research-projects",
        record.id,
        {
          ...record,
          state: admissibilityStatus,
          notes: record.admissibility_notes,
        },
        record,
        {
          onFailure: (error) => notify(error?.message || error, "error"),
        }
      ),
    [update, record, admissibilityStatus, notify]
  );
  return (
    <div>
      <Button
        disableElevation
        endIcon={<ArrowDropDownIcon />}
        color="primary"
        variant="outlined"
        aria-controls="admissibility-status-menu"
        aria-haspopup="true"
        size="small"
        disabled={loading || confirmed}
        style={{
          backgroundColor: STATE_COLORS[admissibilityStatus],
        }}
        onClick={handleClick}
      >
        {get(currentState, "name", "N.D.")}
      </Button>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {nextStates.map((state) => (
          <MenuItem
            key={get(state, "code")}
            onClick={handleChange.bind(this, state)}
          >
            {get(state, "label")}
          </MenuItem>
        ))}
      </Menu>
      {transactionState !== admissibilityStatus && <br />}
      {transactionState !== admissibilityStatus && (
        <Button
          disabled={loading}
          className={classes.confirmButton}
          disableElevation
          color="primary"
          size="small"
          variant="contained"
          onClick={handleConfirmState}
        >
          {translate(
            "resources.research-projects.buttons.confirm_change_state"
          )}
        </Button>
      )}
    </div>
  );
};
export default AdmissibilityStatusInput;
